/* __next_internal_client_entry_do_not_use__ Default,AwardsCarousel auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { Intersection } from '@splidejs/splide-extension-intersection';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/splide/css';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import SvgIcon from 'helpers/SvgIcon/SvgIcon';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const AwardsAndAccreditations = (props)=>{
    const { title, items } = props?.fields?.data?.item || {};
    const titleField = title?.jsonValue;
    const awardItems = items?.targetItems ?? [];
    const { DynamicPlaceholderId, RenderingIdentifier } = props?.params || {};
    const theme = props?.params?.selectTheme;
    const phKey = `codeembed-${DynamicPlaceholderId}`;
    const { base, sectionContentWrapper, sectionTitleCover, sectionTitleWrapper, sectionTitle, sectionDivider, sectionPlaceholderWrapper, cardList, card } = TAILWIND_VARIANTS();
    const isEditing = useIsEditing();
    if (!isEditing && !awardItems.length) return /*#__PURE__*/ _jsx(_Fragment, {});
    return /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ _jsx("section", {
            "data-component": "authorable/shared/lists/awardsandaccreditations",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            ...getTestProps(`component-awardsandaccreditations-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx("div", {
                className: base(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: sectionContentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: sectionTitleCover(),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: sectionTitleWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: sectionTitle(),
                                            field: titleField,
                                            tag: "h2"
                                        }),
                                        titleField?.value ? /*#__PURE__*/ _jsx("span", {
                                            className: sectionDivider()
                                        }) : null
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: sectionPlaceholderWrapper(),
                                    children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                        name: phKey,
                                        rendering: props?.rendering
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: cardList(),
                            "aria-label": "List of awards and accreditations",
                            children: awardItems.map((item, index)=>/*#__PURE__*/ _jsx("li", {
                                    className: card(),
                                    children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                        field: item?.awardImage?.jsonValue,
                                        ...getTestProps(`image-${item.id ?? index}`)
                                    })
                                }, item.id ?? index))
                        })
                    ]
                })
            })
        })
    });
};
const SPLIDE_INTERVAL = 3000;
const SPLIDE_SPEED = 300;
const AwardsAndAccreditationsVariant = (props)=>{
    const { title, items } = props?.fields?.data?.item || {};
    const titleField = title?.jsonValue;
    const awardItems = items?.targetItems ?? [];
    const { DynamicPlaceholderId, RenderingIdentifier } = props?.params || {};
    const theme = props?.params?.selectTheme;
    const phKey = `codeembed-${DynamicPlaceholderId}`;
    const { base, sectionContentWrapper, sectionTitleCarouselCover, sectionTitleCarouselWrapper, sectionTitle, sectionDivider, sectionPlaceholderWrapper, splideList, slideListCard, splideTrackWrapper, pagination, slideCardImage, arrowPrev, arrowNext, arrowIcon, centeredCarouselList, centeredCarouselCard } = TAILWIND_VARIANTS();
    const splideRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(null);
    const isEditing = useIsEditing();
    const slideCount = awardItems.length;
    const isSingleSlide = slideCount === 1;
    const shouldCenterDesktopCards = slideCount <= 3;
    const showDesktopArrows = slideCount >= 4;
    const isPrevDisabled = currentIndex === 0;
    const isNextDisabled = endIndex !== null && currentIndex >= endIndex;
    const onSplideMove = (instance)=>{
        setCurrentIndex(instance.index);
        setEndIndex(instance.Components.Controller.getEnd());
    };
    const splideOptions = {
        autoplay: false,
        gap: '8px',
        arrows: false,
        intersection: {
            inView: {
                autoplay: false
            },
            outView: {
                autoplay: false
            }
        },
        interval: SPLIDE_INTERVAL,
        pagination: !isSingleSlide,
        perMove: 1,
        perPage: isSingleSlide ? 1 : 3,
        omitEnd: true,
        rewind: false,
        speed: SPLIDE_SPEED,
        type: 'slide',
        width: '100%',
        drag: !isSingleSlide,
        rewindByDrag: false,
        padding: isSingleSlide ? {
            left: 0,
            right: 0
        } : {
            right: '30%'
        },
        breakpoints: {
            1024: {
                perPage: isSingleSlide ? 1 : 2,
                padding: isSingleSlide ? {
                    left: 0,
                    right: 0
                } : {
                    right: '26%'
                }
            },
            767: {
                perPage: 1,
                pagination: !isSingleSlide,
                padding: isSingleSlide ? {
                    left: 0,
                    right: 0
                } : {
                    right: '43%'
                }
            }
        }
    };
    if (!isEditing && !awardItems.length) return /*#__PURE__*/ _jsx(_Fragment, {});
    return /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ _jsx("section", {
            "data-component": "authorable/shared/lists/awardsandaccreditations",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            ...getTestProps(`component-awardsandaccreditations-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx("div", {
                className: base(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: sectionContentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: sectionTitleCarouselCover(),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: sectionTitleCarouselWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: sectionTitle({
                                                titleAlignment: 'carousel'
                                            }),
                                            field: titleField,
                                            tag: "h2"
                                        }),
                                        titleField?.value ? /*#__PURE__*/ _jsx("span", {
                                            className: sectionDivider()
                                        }) : null
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: sectionPlaceholderWrapper(),
                                    children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                        name: phKey,
                                        rendering: props?.rendering
                                    })
                                })
                            ]
                        }),
                        shouldCenterDesktopCards && /*#__PURE__*/ _jsx("ul", {
                            className: centeredCarouselList(),
                            "aria-label": "List of awards and accreditations",
                            children: awardItems.map((item, index)=>/*#__PURE__*/ _jsx("li", {
                                    className: centeredCarouselCard(),
                                    children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                        className: slideCardImage(),
                                        field: item?.awardImage?.jsonValue,
                                        ...getTestProps(`image-${item.id ?? index}`)
                                    })
                                }, item.id ?? index))
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: `w-full relative ${shouldCenterDesktopCards ? 'lg:hidden' : ''}`,
                            children: [
                                showDesktopArrows && /*#__PURE__*/ _jsx(ButtonWrapper, {
                                    ctaComponentClass: "default",
                                    ctaVariant: "tertiary",
                                    onClick: ()=>splideRef.current?.go('<'),
                                    className: arrowPrev({
                                        isArrowDisabled: isPrevDisabled
                                    }),
                                    disabled: isPrevDisabled,
                                    "aria-label": "Previous slide",
                                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                                        icon: "prev-arrow",
                                        className: arrowIcon(),
                                        viewBox: "0 0 18 21"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Splide, {
                                    className: isSingleSlide ? 'w-full' : 'lg:w-screen lg:mr-[calc(50%-50vw)]',
                                    ref: (ref)=>{
                                        if (ref?.splide) splideRef.current = ref.splide;
                                    },
                                    extensions: {
                                        Intersection
                                    },
                                    hasTrack: false,
                                    onMounted: onSplideMove,
                                    onMoved: onSplideMove,
                                    onUpdated: onSplideMove,
                                    options: splideOptions,
                                    children: [
                                        /*#__PURE__*/ _jsx(SplideTrack, {
                                            className: splideTrackWrapper({
                                                isSingleSlide
                                            }),
                                            children: awardItems.map((item, index)=>/*#__PURE__*/ _jsx(SplideSlide, {
                                                    className: splideList({
                                                        isSingleSlide
                                                    }),
                                                    children: /*#__PURE__*/ _jsx("div", {
                                                        className: slideListCard({
                                                            isSingleSlide
                                                        }),
                                                        children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                                            className: slideCardImage(),
                                                            field: item?.awardImage?.jsonValue,
                                                            ...getTestProps(`image-${item.id ?? index}`)
                                                        })
                                                    })
                                                }, item.id ?? index))
                                        }),
                                        !isSingleSlide && /*#__PURE__*/ _jsx("ul", {
                                            className: `splide__pagination ${pagination()}`
                                        })
                                    ]
                                }, isSingleSlide ? 'awards-single-slide' : 'awards-carousel'),
                                showDesktopArrows && /*#__PURE__*/ _jsx(ButtonWrapper, {
                                    ctaComponentClass: "default",
                                    ctaVariant: "tertiary",
                                    onClick: ()=>splideRef.current?.go('>'),
                                    className: arrowNext({
                                        isArrowDisabled: isNextDisabled
                                    }),
                                    disabled: isNextDisabled,
                                    "aria-label": "Next slide",
                                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                                        icon: "prev-arrow",
                                        className: arrowIcon(),
                                        viewBox: "0 0 18 21"
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
};
export const Default = withStandardComponentWrapper(AwardsAndAccreditations);
export const AwardsCarousel = withStandardComponentWrapper(AwardsAndAccreditationsVariant);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'flex',
            'bg-component-section-bg',
            'gap-space-between-more',
            'flex-wrap',
            'relative'
        ],
        sectionContentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'h-auto',
            'gap-component-section-container-gutter'
        ],
        sectionTitleCover: [
            'flex',
            'flex-col',
            'lg:flex-row',
            'lg:justify-between',
            'gap-component-section-container-gutter',
            'lg:gap-spacing-spacing-8'
        ],
        sectionTitleCarouselCover: [
            'flex',
            'flex-col',
            'items-center'
        ],
        sectionTitleWrapper: [
            'flex',
            'flex-col',
            'gap-space-between-base',
            'items-center',
            'lg:items-start',
            'lg:basis-1/2',
            'lg:grow-0',
            'lg:shrink-0'
        ],
        sectionTitleCarouselWrapper: [
            'flex',
            'flex-col',
            'gap-space-between-base',
            'items-center'
        ],
        sectionPlaceholderWrapper: [
            'lg:basis-1/2',
            'lg:grow-0',
            'lg:shrink-0',
            'flex',
            'justify-center',
            'lg:justify-end'
        ],
        sectionTitle: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge',
            'text-center'
        ],
        sectionDivider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        cardList: [
            'flex',
            'flex-wrap',
            'align-center',
            'gap-padding-base',
            'justify-center',
            'lg:justify-start'
        ],
        centeredCarouselList: [
            'hidden',
            'lg:flex',
            'w-full',
            'justify-center',
            'gap-padding-base',
            'items-stretch'
        ],
        centeredCarouselCard: [
            'rounded-2xl',
            'bg-white',
            'shadow-[0_0_8px_0_rgba(0,0,0,0.10)]',
            'p-[32px]',
            'max-h-[200px]',
            'h-full',
            'flex',
            'justify-center',
            'min-h-[200px]',
            'w-1/3',
            'max-w-[320px]'
        ],
        splideList: [
            'p-[8px]'
        ],
        slideListCard: [
            'rounded-2xl',
            'bg-white',
            'shadow-[0_0_8px_0_rgba(0,0,0,0.10)]',
            'p-[32px]',
            'max-h-[200px]',
            'h-full',
            'flex',
            'justify-center',
            'min-h-[150px]',
            'md:min-h-[200px]'
        ],
        splideTrackWrapper: [
            'mb-[40px]',
            'w-screen',
            'mr-[calc(50%-50vw)]',
            '!overflow-visible'
        ],
        pagination: [
            'p-0',
            'pt-component-section-container-gutter',
            'flex',
            'justify-center',
            '!relative',
            '[&>li]:p-[6px]',
            '[&>li>button]:w-spacing-spacing-12',
            '[&>li>button]:h-spacing-spacing-12',
            '[&>li>button]:bg-transparent',
            '[&>li>button]:!border',
            '[&>li>button]:!border-color-core-color-1-Navy',
            '[&>li>button]:!border-solid',
            '[&>li>button]:rounded-full',
            '[&>li>button]:m-0',
            '[&>li>button.is-active]:bg-color-core-color-1-Navy',
            '[&>li>button.is-active]:transform-none',
            '[&>li>button.is-active]:opacity-100',
            '[&>li>button.is-active]:w-spacing-spacing-40'
        ],
        arrowPrev: [
            'absolute',
            'top-[39%]',
            '-translate-y-1/2',
            'z-10',
            'border-general-border-width-pagination-button',
            'border-solid',
            'border-component-carousel-utility-bar-button-border',
            'rounded-general-border-radius-tag',
            'text-white',
            '!p-spacing-spacing-12',
            'hover:opacity-90',
            'transition-opacity',
            'hidden',
            'lg:block',
            'lg:left-[-2vw]',
            'xl:!left-[-4.5vw]',
            'bg-component-carousel-utility-bar-button-bg'
        ],
        arrowNext: [
            'absolute',
            'top-[39%]',
            '-translate-y-1/2',
            'lg:right-[-2vw]',
            'right-0',
            'z-10',
            'border-general-border-width-pagination-button',
            'border-solid',
            'border-component-carousel-utility-bar-button-border',
            'rounded-general-border-radius-tag',
            'text-white',
            '!p-spacing-spacing-12',
            'rotate-180',
            'hover:opacity-90',
            'transition-opacity',
            'hidden',
            'lg:block',
            'bg-component-carousel-utility-bar-button-bg'
        ],
        arrowIcon: [
            '!h-[18px]',
            '!w-[18px]'
        ],
        card: [
            'flex',
            'items-center',
            'justify-center',
            'p-padding-micro',
            'w-[100px]',
            'md:w-[120px]',
            'lg:w-[150px]'
        ],
        slideCardImage: [
            'w-auto',
            'object-contain'
        ]
    },
    variants: {
        backgroundLayout: {
            FullWidth: {
                base: [
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:bg-component-section-bg',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:right-[calc(-50vw+50%)]'
                ]
            },
            Content: {
                base: []
            }
        },
        hasContent: {
            true: {
                sectionContentWrapper: 'gap-space-between-more'
            },
            false: {
                sectionContentWrapper: 'gap-0'
            }
        },
        removePaddingTop: {
            true: {
                base: [
                    'pt-0'
                ]
            },
            false: {
                base: [
                    'pt-component-section-container-padding-y'
                ]
            }
        },
        removePaddingBottom: {
            true: {
                base: [
                    'pb-0'
                ]
            },
            false: {
                base: [
                    'pb-component-section-container-padding-y'
                ]
            }
        },
        displayCenter75: {
            true: {
                sectionContentWrapper: [
                    'max-w-layout-container-center70-max-width',
                    'mx-auto'
                ]
            },
            false: {
                sectionContentWrapper: []
            }
        },
        titleAlignment: {
            default: {
                sectionTitle: 'lg:text-left'
            },
            carousel: {
                sectionTitle: 'lg:text-center'
            }
        },
        shouldCenterDesktopCards: {
            true: {
                cardList: [
                    'lg:justify-center'
                ]
            },
            false: {
                cardList: [
                    ''
                ]
            }
        },
        isSingleSlide: {
            true: {
                splideTrackWrapper: [
                    '!w-full',
                    '!mr-0',
                    '[&_.splide__track]:!w-full',
                    '[&_.splide__list]:!w-full'
                ],
                splideList: [
                    '!w-full'
                ],
                slideListCard: [
                    'w-full'
                ]
            },
            false: {
                splideTrackWrapper: [
                    'w-screen',
                    'mr-[calc(50%-50vw)]'
                ]
            }
        },
        isArrowDisabled: {
            true: {
                arrowPrev: [
                    'opacity-50',
                    'cursor-not-allowed'
                ],
                arrowNext: [
                    'opacity-50',
                    'cursor-not-allowed'
                ]
            }
        }
    },
    defaultVariants: {
        hasContent: false,
        removePaddingTop: false,
        removePaddingBottom: false,
        displayCenter75: false,
        backgroundLayout: 'FullWidth',
        titleAlignment: 'default',
        shouldCenterDesktopCards: false,
        isSingleSlide: false,
        isArrowDisabled: false
    }
});
